import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { GraduationCap, Users, Clock, Send, Award } from 'lucide-react';
import { FadeIn, StaggerContainer, StaggerItem } from '@/components/motion';
import { certificationService, CertificationAdminStats, CertificationCohort } from '@/lib/api/services/certification.service';
import { adminService } from '@/lib/api/services/admin.service';

const STATUS_COLORS: Record<string, string> = {
  forming: 'bg-blue-100 text-blue-700',
  full: 'bg-amber-100 text-amber-700',
  submitted: 'bg-purple-100 text-purple-700',
  completed: 'bg-green-100 text-green-700',
};

export function CertificationManagement() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin-certification'],
    queryFn: () => certificationService.getAdminCohorts(),
  });

  // Fetch ALL freelancers from admin endpoint so we can toggle interest for any genius
  const { data: allFreelancers = [], isLoading: isLoadingFreelancers } = useQuery({
    queryKey: ['admin-freelancer-profiles'],
    queryFn: () => adminService.getAllFreelancers(500),
  });

  // Fetch who is already interested
  const { data: interestedList = [], isLoading: isLoadingInterested } = useQuery({
    queryKey: ['admin-interested'],
    queryFn: () => certificationService.getAdminInterested(),
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      certificationService.updateCohortStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-certification'] });
      toast.success('Cohort status updated');
    },
    onError: () => toast.error('Failed to update status'),
  });

  const toggleInterestMutation = useMutation({
    mutationFn: ({ profileId, is_interested }: { profileId: string; is_interested: boolean }) =>
      certificationService.adminToggleInterest(profileId, is_interested),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-interested'] });
      queryClient.invalidateQueries({ queryKey: ['admin-certification'] });
      toast.success('Interest toggled');
    },
    onError: () => toast.error('Failed to toggle interest'),
  });

  const stats: CertificationAdminStats = data?.stats || {
    totalInterested: 0, totalCohorts: 0, forming: 0, full: 0, submitted: 0, completed: 0,
  };
  const cohorts: CertificationCohort[] = data?.cohorts || [];

  // Build a set of profile IDs that are interested
  const interestedProfileIds = new Set(
    interestedList
      .filter((i: any) => i.is_interested)
      .map((i: any) => i.freelancer_profile_id || i.freelancer_profiles?.id)
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <FadeIn className="space-y-6">
      {/* Stats Row */}
      <StaggerContainer className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StaggerItem>
          <Card><CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2 mb-1"><GraduationCap className="h-4 w-4 text-color-4" /><span className="text-xs text-muted-foreground">Interested</span></div>
            <p className="text-2xl font-bold">{stats.totalInterested}</p>
          </CardContent></Card>
        </StaggerItem>
        <StaggerItem>
          <Card><CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2 mb-1"><Users className="h-4 w-4 text-blue-500" /><span className="text-xs text-muted-foreground">Total Cohorts</span></div>
            <p className="text-2xl font-bold">{stats.totalCohorts}</p>
          </CardContent></Card>
        </StaggerItem>
        <StaggerItem>
          <Card><CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2 mb-1"><Clock className="h-4 w-4 text-blue-500" /><span className="text-xs text-muted-foreground">Forming</span></div>
            <p className="text-2xl font-bold">{stats.forming}</p>
          </CardContent></Card>
        </StaggerItem>
        <StaggerItem>
          <Card><CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2 mb-1"><Users className="h-4 w-4 text-amber-500" /><span className="text-xs text-muted-foreground">Full</span></div>
            <p className="text-2xl font-bold">{stats.full}</p>
          </CardContent></Card>
        </StaggerItem>
        <StaggerItem>
          <Card><CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2 mb-1"><Send className="h-4 w-4 text-purple-500" /><span className="text-xs text-muted-foreground">Submitted</span></div>
            <p className="text-2xl font-bold">{stats.submitted}</p>
          </CardContent></Card>
        </StaggerItem>
        <StaggerItem>
          <Card><CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2 mb-1"><Award className="h-4 w-4 text-green-500" /><span className="text-xs text-muted-foreground">Completed</span></div>
            <p className="text-2xl font-bold">{stats.completed}</p>
          </CardContent></Card>
        </StaggerItem>
      </StaggerContainer>

      {/* Genius Interest Management */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <GraduationCap className="h-5 w-5" />
            Genius Certification Interest
          </CardTitle>
          <p className="text-sm text-muted-foreground">Toggle certification interest on/off for any Genius. Opted-in Geniuses appear in the Certification Hub.</p>
        </CardHeader>
        <CardContent>
          {isLoadingFreelancers || isLoadingInterested ? (
            <Skeleton className="h-32" />
          ) : allFreelancers.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No freelancer profiles found</p>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {(allFreelancers as any[]).map((fp: any) => {
                const isOptedIn = interestedProfileIds.has(fp.id);
                const initials = (fp.display_name || '??').split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
                return (
                  <div key={fp.id} className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 border">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-9 w-9">
                        <AvatarImage src={fp.profile_picture_url || undefined} />
                        <AvatarFallback className="text-xs bg-color-1/10 text-color-1">{initials}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium">{fp.display_name || 'Unnamed'}</p>
                        <p className="text-xs text-muted-foreground">{fp.headline || fp.location || 'No details'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isOptedIn && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-semibold">Opted In</span>
                      )}
                      <Switch
                        checked={isOptedIn}
                        onCheckedChange={(checked) => toggleInterestMutation.mutate({ profileId: fp.id, is_interested: checked })}
                        disabled={toggleInterestMutation.isPending}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cohorts Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">All Cohorts</CardTitle>
        </CardHeader>
        <CardContent>
          {cohorts.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No cohorts created yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 font-medium text-muted-foreground">Name</th>
                    <th className="pb-2 font-medium text-muted-foreground">Organizer</th>
                    <th className="pb-2 font-medium text-muted-foreground">Members</th>
                    <th className="pb-2 font-medium text-muted-foreground">Status</th>
                    <th className="pb-2 font-medium text-muted-foreground">Created</th>
                    <th className="pb-2 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {cohorts.map((cohort) => {
                    const memberCount = cohort.certification_cohort_members?.[0]?.count || 0;
                    const organizerName = (cohort.freelancer_profiles as any)?.display_name || 'Unknown';
                    return (
                      <tr key={cohort.id} className="border-b last:border-0">
                        <td className="py-3 font-medium">{cohort.name}</td>
                        <td className="py-3 text-muted-foreground">{organizerName}</td>
                        <td className="py-3">
                          <span className="font-semibold">{memberCount}</span>
                          <span className="text-muted-foreground">/{cohort.max_members}</span>
                        </td>
                        <td className="py-3">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_COLORS[cohort.status]}`}>
                            {cohort.status}
                          </span>
                        </td>
                        <td className="py-3 text-muted-foreground">
                          {new Date(cohort.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-3">
                          <Select
                            value={cohort.status}
                            onValueChange={(status) => updateStatusMutation.mutate({ id: cohort.id, status })}
                          >
                            <SelectTrigger className="h-7 w-32 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="forming">Forming</SelectItem>
                              <SelectItem value="full">Full</SelectItem>
                              <SelectItem value="submitted">Submitted</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </FadeIn>
  );
}
